import { useState } from "react";
import { CATEGORIES } from "../utils/constants";
import { uid } from "../utils/helpers";
import styles from "../styles/theme";

export default function PersonFormModal({ person, people, onSave, onClose }) {
  const [form, setForm] = useState({
    name: person?.name || "",
    relationship: person?.relationship || "",
    category: person?.category || "social",
    meetingPlace: person?.meetingPlace || "",
    significantOther: person?.significantOther || "",
    dateMet: person?.dateMet || "",
    birthdate: person?.birthdate || "",
    phoneNumber: person?.phoneNumber || "",
    phoneNumberGiven: person?.phoneNumberGiven || "",
    jobDescription: person?.jobDescription || "",
    profilePicture: person?.profilePicture || "",
    intimatePartner: person?.intimatePartner || false,
    intimateStart: person?.intimateStart || "",
    intimateEnd: person?.intimateEnd || "",
    nightsTogether: person?.nightsTogether || [],
    notes: person?.notes || "",
    parentId: person?.parentId || "",
  });
  const [saving, setSaving] = useState(false);
  const [nightDate, setNightDate] = useState("");
  const [nightLabel, setNightLabel] = useState("");

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500000) {
      alert("Image must be under 500KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => set("profilePicture", ev.target.result);
    reader.readAsDataURL(file);
  };

  const addNight = () => {
    if (!nightDate) return;
    set("nightsTogether", [
      ...form.nightsTogether,
      { id: uid(), date: nightDate, label: nightLabel },
    ]);
    setNightDate("");
    setNightLabel("");
  };

  const removeNight = (id) => {
    set(
      "nightsTogether",
      form.nightsTogether.filter((n) => n.id !== id),
    );
  };

  const updateNight = (id, field, val) => {
    set(
      "nightsTogether",
      form.nightsTogether.map((n) =>
        n.id === id ? { ...n, [field]: val } : n,
      ),
    );
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave({
      ...form,
      name: form.name.trim(),
      relationship: form.relationship.trim(),
    });
    setSaving(false);
  };

  const sameCategoryPeople = people.filter(
    (p) => p.category === form.category && p.id !== person?.id,
  );

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            {person ? "Edit Person" : "Add New Person"}
          </h2>
          <button onClick={onClose} style={styles.modalClose}>
            ✕
          </button>
        </div>

        <div style={styles.modalBody}>
          {/* Profile Picture */}
          <div
            style={{
              ...styles.fieldGroup,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div style={styles.profileUploadArea}>
              {form.profilePicture ? (
                <img
                  src={form.profilePicture}
                  alt="Profile"
                  style={styles.profilePreview}
                />
              ) : (
                <span style={styles.profilePlaceholder}>📷</span>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.fieldLabel}>Profile Picture</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={styles.fileInput}
              />
              {form.profilePicture && (
                <button
                  onClick={() => set("profilePicture", "")}
                  style={styles.removePhotoBtn}
                >
                  Remove photo
                </button>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Name *</label>
              <input
                style={styles.input}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. John Smith"
                autoFocus
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Relationship *</label>
              <input
                style={styles.input}
                value={form.relationship}
                onChange={(e) => set("relationship", e.target.value)}
                placeholder="e.g. Project Manager, Cousin, Gym buddy"
              />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>Category</label>
            <div style={styles.categoryPicker}>
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => set("category", c.id)}
                  style={{
                    ...styles.categoryPickerBtn,
                    ...(form.category === c.id
                      ? {
                          background: c.color,
                          color: "#fff",
                          borderColor: c.color,
                        }
                      : {}),
                  }}
                >
                  {c.icon} {c.label}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Phone Number</label>
              <input
                style={styles.input}
                type="tel"
                value={form.phoneNumber}
                onChange={(e) => set("phoneNumber", e.target.value)}
                placeholder="e.g. (555) 123-4567"
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Phone Number Given</label>
              <select
                style={styles.input}
                value={form.phoneNumberGiven}
                onChange={(e) => set("phoneNumberGiven", e.target.value)}
              >
                <option value="">— None —</option>
                <option value="primary_a">Primary (A)</option>
                <option value="secondary_m">Secondary (M)</option>
              </select>
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>Job Description</label>
            <input
              style={styles.input}
              value={form.jobDescription}
              onChange={(e) => set("jobDescription", e.target.value)}
              placeholder="e.g. Senior Software Engineer at Google"
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Meeting Place</label>
              <input
                style={styles.input}
                value={form.meetingPlace}
                onChange={(e) => set("meetingPlace", e.target.value)}
                placeholder="e.g. Coffee shop on 5th Ave"
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Significant Other</label>
              <input
                style={styles.input}
                value={form.significantOther}
                onChange={(e) => set("significantOther", e.target.value)}
                placeholder="e.g. Married to Lisa"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <div style={styles.labelWithAction}>
                <label style={styles.fieldLabel}>Date Met</label>
                <button
                  type="button"
                  onClick={() =>
                    set("dateMet", new Date().toISOString().slice(0, 10))
                  }
                  style={styles.todayBtn}
                >
                  Today
                </button>
              </div>
              <input
                style={styles.input}
                type="date"
                value={form.dateMet}
                onChange={(e) => set("dateMet", e.target.value)}
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Birthdate</label>
              <input
                style={styles.input}
                type="date"
                value={form.birthdate}
                onChange={(e) => set("birthdate", e.target.value)}
              />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.intimatePartner}
                onChange={(e) => {
                  set("intimatePartner", e.target.checked);
                  if (!e.target.checked) {
                    set("intimateStart", "");
                    set("intimateEnd", "");
                  }
                }}
                style={styles.checkbox}
              />
              <span style={styles.checkboxLabel}>Intimate Partner</span>
            </label>
            {form.intimatePartner && (
              <div style={{ ...styles.formRow, marginTop: 10 }}>
                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>Start Date</label>
                  <input
                    style={styles.input}
                    type="date"
                    value={form.intimateStart}
                    onChange={(e) => set("intimateStart", e.target.value)}
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>End Date</label>
                  <input
                    style={styles.input}
                    type="date"
                    value={form.intimateEnd}
                    onChange={(e) => set("intimateEnd", e.target.value)}
                  />
                </div>
              </div>
            )}
            {form.intimatePartner && (
              <div style={{ marginTop: 14 }}>
                <label style={styles.fieldLabel}>Nights Together</label>
                <div style={styles.nightsAddRow}>
                  <input
                    type="date"
                    value={nightDate}
                    onChange={(e) => setNightDate(e.target.value)}
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <input
                    type="text"
                    value={nightLabel}
                    onChange={(e) => setNightLabel(e.target.value)}
                    placeholder="Label (optional)"
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <button
                    onClick={addNight}
                    disabled={!nightDate}
                    style={styles.nightsAddBtn}
                  >
                    +
                  </button>
                </div>
                {form.nightsTogether.length > 0 && (
                  <div style={styles.nightsList}>
                    {[...form.nightsTogether]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((n) => (
                        <div key={n.id} style={styles.nightsItem}>
                          <input
                            type="date"
                            value={n.date}
                            onChange={(e) =>
                              updateNight(n.id, "date", e.target.value)
                            }
                            style={{ ...styles.nightsInput, width: 140 }}
                          />
                          <input
                            type="text"
                            value={n.label}
                            onChange={(e) =>
                              updateNight(n.id, "label", e.target.value)
                            }
                            placeholder="Label"
                            style={{ ...styles.nightsInput, flex: 1 }}
                          />
                          <button
                            onClick={() => removeNight(n.id)}
                            style={styles.nightsRemoveBtn}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    <div style={styles.nightsCount}>
                      {form.nightsTogether.length}{" "}
                      {form.nightsTogether.length === 1 ? "entry" : "entries"}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {sameCategoryPeople.length > 0 && (
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>
                Link to (optional — for tree hierarchy)
              </label>
              <select
                style={styles.input}
                value={form.parentId}
                onChange={(e) => set("parentId", e.target.value)}
              >
                <option value="">— No link (top-level) —</option>
                {sameCategoryPeople.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.relationship})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>Notes</label>
            <textarea
              style={{ ...styles.input, minHeight: 80, resize: "vertical" }}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Anything you'd like to remember..."
            />
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.cancelBtn}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!form.name.trim() || saving}
            style={styles.saveBtn}
          >
            {saving ? "Saving..." : person ? "Update" : "Add Person"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── BULK ADD MODAL ──────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
